// src/components/mdx/client/visualizations/movingAverageData.ts

/**
 * Shared data helpers for the Moving Average Purpose chart. Extracting the
 * logic lets the client component memoise expensive computations and enables
 * deterministic unit tests that exercise the different noise regimes.
 */

/**
 * Data point structure for time series data with moving average support
 * @property {number} day - The day/time index of the data point
 * @property {number} value - The raw value at this point
 * @property {number} [movingAverage] - The calculated moving average value (optional)
 * @property {boolean} [isHighlight] - Whether this point should be highlighted (optional)
 * @property {'high' | 'low'} [highlightType] - Type of highlight if isHighlight is true (optional)
 */
export interface MovingAverageDataPoint {
  day: number
  value: number
  movingAverage?: number
  isHighlight?: boolean
  highlightType?: 'high' | 'low'
}

/**
 * Function type for random number generation
 * @returns {number} A random number between 0 and 1
 */
type RandomSource = () => number

/**
 * Generates simulated data with different noise patterns to demonstrate moving
 * average benefits. The data is divided into five phases with distinct characteristics:
 * - Stable phase (20%): Moderate noise around a stable baseline
 * - Trending up phase (25%): Strong uptrend with high noise
 * - False downtrend phase (15%): Flat underlying trend with asymmetric noise
 * - Volatile phase (20%): Hidden uptrend masked by extreme volatility
 * - Trending down phase (20%): Clear downtrend with moderate noise
 * 
 * @param {number} numPoints - Number of data points to generate
 * @param {RandomSource} [rng=Math.random] - Random number generator function (defaults to Math.random)
 * @returns {MovingAverageDataPoint[]} Array of data points with highlighted high/low values per phase
 */
export function generateMovingAverageData(
  numPoints: number,
  rng: RandomSource = Math.random
): MovingAverageDataPoint[] {
  const data: MovingAverageDataPoint[] = []

  // Phase durations (as percentages of total timeline)
  const stablePhase = 0.2 // 20% - Stable with moderate noise
  const trendingUpPhase = 0.25 // 25% - Trending up with high noise
  const falseDownPhase = 0.15 // 15% - False downtrend (noisy but flat underlying)
  const volatilePhase = 0.2 // 20% - Highly volatile with hidden uptrend
  const trendingDownPhase = 0.2 // 20% - Clear downtrend with moderate noise
  void trendingDownPhase

  // Calculate phase boundaries
  const stablePhaseEnd = Math.floor(numPoints * stablePhase)
  const trendingUpPhaseEnd = Math.floor(numPoints * (stablePhase + trendingUpPhase))
  const falseDownPhaseEnd = Math.floor(numPoints * (stablePhase + trendingUpPhase + falseDownPhase))
  const volatilePhaseEnd = Math.floor(
    numPoints * (stablePhase + trendingUpPhase + falseDownPhase + volatilePhase)
  )

  // Base value to start with
  let baseValue = 50

  // Track min/max values for each phase to highlight later
  const phaseHighLowPoints = {
    stable: { high: { value: -Infinity, index: -1 }, low: { value: Infinity, index: -1 } },
    trendingUp: { high: { value: -Infinity, index: -1 }, low: { value: Infinity, index: -1 } },
    falseDown: { high: { value: -Infinity, index: -1 }, low: { value: Infinity, index: -1 } },
    volatile: { high: { value: -Infinity, index: -1 }, low: { value: Infinity, index: -1 } },
    trendingDown: { high: { value: -Infinity, index: -1 }, low: { value: Infinity, index: -1 } }
  }

  for (let i = 0; i < numPoints; i++) {
    let finalValue = 0
    let currentPhase = ''

    // Phase 1: Stable with moderate noise
    if (i < stablePhaseEnd) {
      currentPhase = 'stable'
      // Stable underlying trend with moderate noise
      baseValue = 50 + Math.sin(i * 0.3) * 3 // Small sine wave for minor natural variation
      const noise = (rng() - 0.5) * 25 // Increased noise
      finalValue = baseValue + noise
    }
    // Phase 2: Trending up with high noise
    else if (i < trendingUpPhaseEnd) {
      currentPhase = 'trendingUp'
      // Calculate progress through this phase
      const phaseProgress = (i - stablePhaseEnd) / (trendingUpPhaseEnd - stablePhaseEnd)

      // Strong uptrend with high noise
      baseValue = 50 + phaseProgress * 35 // Linear increase from ~50 to ~85

      // More extreme noise with occasional large spikes
      let noise
      if (rng() > 0.9) {
        // 10% chance of extreme value
        noise = (rng() - 0.5) * 45
      } else {
        // 90% chance of regular high noise
        noise = (rng() - 0.5) * 30
      }

      finalValue = baseValue + noise
    }
    // Phase 3: False downtrend (noisy but flat underlying)
    else if (i < falseDownPhaseEnd) {
      currentPhase = 'falseDown'
      // Calculate progress through this phase
      const phaseProgress = (i - trendingUpPhaseEnd) / (falseDownPhaseEnd - trendingUpPhaseEnd)

      // Flat underlying trend with asymmetric noise (more negative than positive)
      baseValue = 85 - phaseProgress * 5 // Very slight actual decline

      // Asymmetric noise to create illusion of downtrend
      const asymmetry = 0.75 // Increased bias toward negative noise
      const rawNoise = rng()
      const adjustedNoise =
        rawNoise > asymmetry
          ? (rawNoise - asymmetry) * 25 // Positive noise (less frequent, smaller)
          : (rawNoise - asymmetry) * 45 // Negative noise (more frequent, larger)

      finalValue = baseValue + adjustedNoise
    }
    // Phase 4: Highly volatile with hidden uptrend
    else if (i < volatilePhaseEnd) {
      currentPhase = 'volatile'
      const phaseProgress = (i - falseDownPhaseEnd) / (volatilePhaseEnd - falseDownPhaseEnd)

      // Hidden uptrend masked by extreme volatility
      baseValue = 80 + phaseProgress * 20 // Uptrend from ~80 to ~100

      // Volatility spikes with varying severity
      let noise
      if (rng() > 0.8) {
        // 20% chance of extreme volatility
        noise = (rng() - 0.5) * 70
      } else if (rng() > 0.4) {
        // 40% chance of moderate volatility
        noise = (rng() - 0.5) * 40
      } else {
        // 40% chance of mild volatility
        noise = (rng() - 0.5) * 25
      }

      finalValue = baseValue + noise
    }
    // Phase 5: Clear downtrend with moderate noise
    else {
      currentPhase = 'trendingDown'
      const phaseProgress = (i - volatilePhaseEnd) / (numPoints - volatilePhaseEnd)

      // Downtrend with moderate noise
      baseValue = 100 - phaseProgress * 45 // Decline from ~100 to ~55

      // Moderate noise with occasional spikes
      let noise = (rng() - 0.5) * 20
      if (rng() > 0.85) {
        // 15% chance of stronger noise spike
        noise = (rng() - 0.5) * 35
      }

      finalValue = baseValue + noise
    }

    const phaseKey = currentPhase as keyof typeof phaseHighLowPoints
    if (finalValue > phaseHighLowPoints[phaseKey].high.value) {
      phaseHighLowPoints[phaseKey].high = { value: finalValue, index: i }
    }
    if (finalValue < phaseHighLowPoints[phaseKey].low.value) {
      phaseHighLowPoints[phaseKey].low = { value: finalValue, index: i }
    }

    data.push({
      day: i,
      value: finalValue
    })
  }

  // Mark the high and low points for each phase
  Object.values(phaseHighLowPoints).forEach(phase => {
    if (phase.high.index >= 0) {
      const highPoint = data[phase.high.index]
      if (highPoint) {
        highPoint.isHighlight = true
        highPoint.highlightType = 'high'
      }
    }

    if (phase.low.index >= 0) {
      const lowPoint = data[phase.low.index]
      if (lowPoint) {
        lowPoint.isHighlight = true
        lowPoint.highlightType = 'low'
      }
    }
  })

  return data
}

/**
 * Calculates moving average for the data series using a sliding window approach.
 * For each point, computes the average of values within the window ending at that point.
 * 
 * @param {MovingAverageDataPoint[]} data - Raw data points to process
 * @param {number} windowSize - Size of the moving average window (number of points to average)
 * @returns {MovingAverageDataPoint[]} Data with moving average values added to each point
 */
export function calculateMovingAverageSeries(
  data: MovingAverageDataPoint[],
  windowSize: number
): MovingAverageDataPoint[] {
  return data.map((point, index) => {
    const startIndex = Math.max(0, index - windowSize + 1)
    const window = data.slice(startIndex, index + 1)
    const sum = window.reduce((acc, val) => acc + val.value, 0)

    return {
      ...point,
      movingAverage: sum / window.length
    }
  })
}